import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  LinearProgress,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  CaregiverProfileCompletion,
  ProfileCompletionBreakdownItem,
  ProfileCompletionFieldKey,
  profileCompletionService,
  PublicProfileCompletion,
} from '../services/profileCompletionService';

// FEAT-040 — Profile Completion Meter (MVP-8.4, MVP-18.15).
//
// Two render modes:
//   - `caregiver`: shows full meter + warm "next step" list for the
//     authenticated caregiver, with deep links into each section.
//   - `public`:   shows a compact "85% complete" badge for careseekers
//     viewing a caregiver's public profile (no breakdown leaked).
//
// Copy follows §1.2 elderly-friendly tone: "Next step", "Almost there",
// "X more to go" — never "incomplete", "missing", or "fail". ARIA
// labels per §15 announce both the percentage and the meter intent.

const DEEP_LINKS: Record<ProfileCompletionFieldKey, string | null> = {
  bio: '#bio',
  avatar: '#bio',
  services: '#services',
  availability: '#availability',
  credentialEntry: '#certifications',
  acceptedCredentialDocuments: '/training',
  completedLearningModules: '/training',
};

const NEXT_STEP_HINTS: Record<ProfileCompletionFieldKey, string> = {
  bio: 'Add a few warm sentences about who you are.',
  avatar: 'Upload a friendly photo so families can recognize you.',
  services: 'List the kinds of help you love to offer.',
  availability: 'Share the days and times you usually have free.',
  credentialEntry: 'Add the license or credential number you use.',
  acceptedCredentialDocuments: 'Upload a credential document for our team to confirm.',
  completedLearningModules: 'Finish a Careva learning module when you have a moment.',
};

function getEncouragement(percentage: number): string {
  if (percentage >= 100) {
    return 'Wonderful — your profile is all set. Thank you for the care you bring!';
  }
  if (percentage >= 75) {
    return 'Almost there! A couple of small steps and you are done.';
  }
  if (percentage >= 40) {
    return 'You are well on your way. Here are the next steps that families love to see.';
  }
  return 'A warm welcome. Here are a few next steps to share who you are.';
}

export type ProfileCompletionMeterMode = 'caregiver' | 'public';

interface ProfileCompletionMeterCommonProps {
  /** Optional override of the data fetcher (used in tests/stories). */
  initialData?: CaregiverProfileCompletion | PublicProfileCompletion;
  /** Optional test id override. */
  testId?: string;
}

interface CaregiverProfileCompletionMeterProps extends ProfileCompletionMeterCommonProps {
  mode: 'caregiver';
}

interface PublicProfileCompletionMeterProps extends ProfileCompletionMeterCommonProps {
  mode: 'public';
  caregiverId: string;
}

export type ProfileCompletionMeterProps =
  | CaregiverProfileCompletionMeterProps
  | PublicProfileCompletionMeterProps;

function isCaregiverData(
  data: CaregiverProfileCompletion | PublicProfileCompletion,
): data is CaregiverProfileCompletion {
  return Array.isArray((data as CaregiverProfileCompletion).breakdown);
}

const ProfileCompletionMeter: React.FC<ProfileCompletionMeterProps> = (props) => {
  const theme = useTheme();
  const [data, setData] = useState<
    CaregiverProfileCompletion | PublicProfileCompletion | null
  >(props.initialData ?? null);
  const [loading, setLoading] = useState(!props.initialData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (props.initialData) {
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const load = async () => {
      try {
        if (props.mode === 'caregiver') {
          const result = await profileCompletionService.getOwnCompletion();
          if (!cancelled) setData(result);
        } else {
          const result = await profileCompletionService.getPublicCompletion(
            props.caregiverId,
          );
          if (!cancelled) setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof Error
              ? err.message
              : 'Could not load profile completion right now',
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [props]);

  const percentage = data?.percentage ?? 0;
  const encouragement = useMemo(() => getEncouragement(percentage), [percentage]);
  const testId = props.testId ?? `profile-completion-meter-${props.mode}`;

  if (loading) {
    return (
      <Box
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}
        data-testid={`${testId}-loading`}
      >
        <CircularProgress size={24} aria-label="Loading profile completion" />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="info" data-testid={`${testId}-error`}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return null;
  }

  // PUBLIC: compact badge for careseekers viewing the caregiver profile.
  if (props.mode === 'public') {
    return (
      <Chip
        icon={<EmojiEventsIcon aria-hidden="true" />}
        color={percentage >= 100 ? 'success' : 'primary'}
        variant={percentage >= 100 ? 'filled' : 'outlined'}
        label={`Profile ${percentage}% complete`}
        aria-label={`Caregiver profile is ${percentage} percent complete`}
        data-testid={testId}
        sx={{ fontWeight: 600 }}
      />
    );
  }

  // CAREGIVER: full meter + warm next-step list with deep links.
  if (!isCaregiverData(data)) {
    return null;
  }

  const remaining = data.breakdown.filter((item) => !item.complete);

  return (
    <Card data-testid={testId}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          {percentage >= 100 ? (
            <CheckCircleIcon color="success" aria-hidden="true" />
          ) : (
            <StarOutlineIcon color="primary" aria-hidden="true" />
          )}
          <Typography variant="h6" component="h2">
            Your profile: {percentage}% complete
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={percentage}
          sx={{
            height: 10,
            borderRadius: 5,
            mb: 2,
            backgroundColor: theme.palette.action.hover,
          }}
          aria-label={`Your profile is ${percentage} percent complete`}
          data-testid={`${testId}-bar`}
        />
        <Typography variant="body1" sx={{ mb: 2 }}>
          {encouragement}
        </Typography>
        {remaining.length > 0 && (
          <List
            dense
            aria-label="Next steps to share more of your story"
            data-testid={`${testId}-next-steps`}
          >
            {remaining.map((item: ProfileCompletionBreakdownItem) => {
              const link = DEEP_LINKS[item.key];
              const hint = NEXT_STEP_HINTS[item.key];
              return (
                <ListItem key={item.key} alignItems="flex-start">
                  <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                    <StarOutlineIcon color="primary" fontSize="small" aria-hidden="true" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      link ? (
                        <Link
                          href={link}
                          underline="hover"
                          sx={{ fontWeight: 600 }}
                          aria-label={`Next step: ${item.label}`}
                          data-testid={`${testId}-step-${item.key}`}
                        >
                          {item.label}
                        </Link>
                      ) : (
                        <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                      )
                    }
                    secondary={hint}
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

export default ProfileCompletionMeter;
